import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import toast from 'react-hot-toast'

import FormControl from 'Design/components/FormControl'
import Label from 'Design/components/Label'
import Input from 'Design/components/Input'
import SwitchInput from 'Design/components/SwitchInput'
import Button from 'Design/components/Button'
import FormErrorText from 'Design/components/FormErrorText'

import Tags from '../../../../../components/Tags'

import { useCreateGuideMutation, useUploadImageMutation } from '../guides.service'
import GuideLayout, { GuideMain, GuideSidebar, GuideTitle } from '../GuideLayout'
import createGuideFormValidationSchema from './create-guide-form-validation-schema'
import StepEditor from '../StepEditor'
import parseErrorMessage from 'Utils/error-message-parser'

const TagsInput = ({ field: { value, onChange } }) => {
  const handleTagCreate = (tag) => {
    onChange([...value, tag])
  }

  const handleTagRemove = (tag) => {
    const newTags = value.filter((t) => t !== tag.value)
    onChange(newTags)
  }

  return <Tags value={value} creatable onCreate={handleTagCreate} onRemove={handleTagRemove} />
}

const CreateGuide = () => {
  const {
    register,
    control,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm({
    defaultValues: {
      name: '',
      content: '',
      isActive: true,
      tags: []
    },
    resolver: yupResolver(createGuideFormValidationSchema)
  })

  const navigate = useNavigate()
  const [createGuide, createGuideResult] = useCreateGuideMutation()
  const [uploadImage, uploadImageResult] = useUploadImageMutation()

  const onSubmit = async (data) => {
    const payload = {
      name: data.name,
      content: data.content,
      is_active: data.isActive,
      tags: data.tags.map((tag) => tag.value)
    }

    try {
      const promise = createGuide(payload).unwrap()
      toast.promise(promise, {
        loading: 'Creating guide',
        success: 'Guide created successfully',
        error: parseErrorMessage('Unable to create guide. Try again..')
      })

      const guide = await promise
      navigate('../' + guide.id, { replace: true })
    } catch {}
  }

  const handleCancelClick = () => {
    navigate('../')
  }

  const disableSubmitButton = isSubmitting || createGuideResult.isLoading || uploadImageResult.isLoading

  return (
    <GuideLayout>
      <GuideMain>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormControl isInvalid={errors.name}>
            <GuideTitle>
              <Label>Guide Title</Label>
              <Input {...register('name')} />
              {errors.name && <FormErrorText>{errors.name.message}</FormErrorText>}
            </GuideTitle>
          </FormControl>

          <FormControl isInvalid={errors.content}>
            <Controller
              control={control}
              name='content'
              render={({ field: { value, onChange } }) => {
                return <StepEditor uploadImage={uploadImage} defaultValue={value} onChange={onChange} />
              }}
            />
            {errors.content && <FormErrorText>{errors.content.message}</FormErrorText>}
          </FormControl>

          <Button variant='primary' type='submit' disabled={disableSubmitButton}>
            Save
          </Button>
          <Button variant='link-muted' onClick={handleCancelClick}>
            Cancel
          </Button>
        </form>
      </GuideMain>
      <GuideSidebar>
        <Controller
          control={control}
          name='isActive'
          render={({ field: { value, onChange } }) => {
            return (
              <SwitchInput checked={value} onChange={onChange}>
                Published
              </SwitchInput>
            )
          }}
        />
        <Controller control={control} name='tags' render={TagsInput} />
      </GuideSidebar>
    </GuideLayout>
  )
}

export default CreateGuide
